import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { projectsAPI, employeesAPI, tasksAPI } from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────
const fmt = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};
const daysAgo = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  return Math.floor((Date.now() - d) / 86400000);
};
const initials = (name = '') =>
  name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

// Severity colour for overdue days
const overdueColor = (days) => {
  if (days >= 30) return { bg: '#fef2f2', text: '#991b1b', bar: '#dc2626' };
  if (days >= 14) return { bg: '#fff7ed', text: '#9a3412', bar: '#ea580c' };
  return { bg: '#fffbeb', text: '#92400e', bar: '#f59e0b' };
};

// ── Mini bar chart component (no library) ─────────────────────────
const MiniDonut = ({ pct, size = 56, stroke = 6, color }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${filled} ${circ}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }} />
    </svg>
  );
};

// ── Animated counter ──────────────────────────────────────────────
const AnimatedNumber = ({ value }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let start = 0;
    const end = Number(value);
    if (end === 0) { setDisplay(0); return; }
    const duration = 600;
    const step = Math.ceil(end / (duration / 16));
    clearInterval(ref.current);
    ref.current = setInterval(() => {
      start += step;
      if (start >= end) { setDisplay(end); clearInterval(ref.current); }
      else setDisplay(start);
    }, 16);
    return () => clearInterval(ref.current);
  }, [value]);
  return <span>{display}</span>;
};

// ── RAG dot ───────────────────────────────────────────────────────
const RAG_CFG = {
  Green: { color: '#16a34a', bg: '#dcfce7', label: 'Green' },
  Amber: { color: '#d97706', bg: '#fef3c7', label: 'Amber' },
  Red:   { color: '#dc2626', bg: '#fee2e2', label: 'Red' },
};
const RagDot = ({ rag }) => {
  const c = RAG_CFG[rag] || RAG_CFG.Green;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:'5px',
      padding:'3px 10px', borderRadius:'20px', background:c.bg,
      fontSize:'12px', fontWeight:'700', color:c.color }}>
      <span style={{ width:8, height:8, borderRadius:'50%', background:c.color, display:'inline-block' }} />
      {c.label}
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [overallocated, setOverallocated] = useState([]);
  const [unplannedData, setUnplannedData] = useState({ tasks:[], total_unplanned_tasks:0, total_planned_hours:0, total_actual_hours:0 });
  const [showUnplannedModal, setShowUnplannedModal] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false); // include completed projects in stats

  // All sections collapsed by default — expand individually
  const [sections, setSections] = useState({
    overdueProjects:  false,
    overdueTasks:     false,
    overallocated:    false,
    projects:         false,
  });
  const toggleSection = (key) => setSections(s => ({ ...s, [key]: !s[key] }));

  // Per-employee expand state inside over-allocated
  const [expandedEmps, setExpandedEmps] = useState({});
  const toggleEmp = (id) => setExpandedEmps(s => ({ ...s, [id]: !s[id] }));
  const [selectedRag, setSelectedRag] = useState(null); // 'Green'|'Amber'|'Red'|null
  const toggleRag = (r) => setSelectedRag(prev => prev === r ? null : r);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const [projRes, overdueRes, overallocRes, unplannedRes] = await Promise.allSettled([
      projectsAPI.getAll(),
      tasksAPI.getOverdue(),
      employeesAPI.getOverallocated(),
      tasksAPI.getUnplanned(),
    ]);
    setProjects(projRes.status === 'fulfilled' ? projRes.value.data : []);
    setOverdueTasks(overdueRes.status === 'fulfilled' ? overdueRes.value.data : []);
    setOverallocated(overallocRes.status === 'fulfilled' ? overallocRes.value.data : []);
    setUnplannedData(unplannedRes.status === 'fulfilled'
      ? unplannedRes.value.data
      : { tasks:[], total_unplanned_tasks:0, total_planned_hours:0, total_actual_hours:0 });
    setLoading(false);
  };

  const today    = new Date();
  const todayStr = today.toISOString().split('T')[0]; // 'YYYY-MM-DD' for string comparisons

  // Split projects into active (Active + On Hold) and completed (Completed + Cancelled)
  const ACTIVE_STATUSES    = ['Active', 'On Hold'];
  const COMPLETED_STATUSES = ['Completed', 'Cancelled'];
  const activeProjects     = projects.filter(p => ACTIVE_STATUSES.includes(p.status));
  const completedProjects  = projects.filter(p => COMPLETED_STATUSES.includes(p.status));

  // Dashboard operates on active projects by default; toggle includes completed
  const dashProjects   = showCompleted ? projects : activeProjects;
  const overdueProjects = dashProjects.filter(p =>
    p.planned_end_date && !COMPLETED_STATUSES.includes(p.status) && new Date(p.planned_end_date) < today
  );
  const totalTasks     = dashProjects.reduce((s, p) => s + (p.total_tasks || 0), 0);
  const completedTasks = dashProjects.reduce((s, p) => s + (p.completed_tasks || 0), 0);
  const completionPct  = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // RAG breakdown — active projects only (completed projects are always Green, not useful here)
  const ragCounts = activeProjects.reduce((acc, p) => {
    const r = p.rag || 'Green';
    acc[r] = (acc[r] || 0) + 1;
    return acc;
  }, { Green: 0, Amber: 0, Red: 0 });

  // Overdue tasks sorted by days overdue desc
  const sortedOverdue = [...overdueTasks].sort((a, b) =>
    daysAgo(b.planned_end) - daysAgo(a.planned_end));



  if (loading) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:'16px' }}>
        <div style={{ width:48, height:48, border:'4px solid #e2e8f0', borderTopColor:'#3b82f6', borderRadius:'50%',
          animation:'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ color:'#64748b', fontSize:'14px', fontWeight:'500' }}>Loading dashboard…</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily:'inherit' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom:'28px', display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h2 style={{ fontSize:'30px', fontWeight:'800', color:'#0f172a', margin:0 }}>Dashboard</h2>
          <p style={{ color:'#64748b', marginTop:'4px', fontSize:'14px' }}>
            {new Date().toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
          </p>
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <button
            onClick={() => setShowCompleted(c => !c)}
            style={{
              display:'flex', alignItems:'center', gap:'6px',
              padding:'8px 16px', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer',
              border: showCompleted ? '1px solid #86efac' : '1px solid #e2e8f0',
              background: showCompleted ? '#f0fdf4' : '#fff',
              color: showCompleted ? '#16a34a' : '#64748b',
              transition:'all 0.15s',
            }}
          >
            {showCompleted ? '✅ Including Completed' : '📁 Active Only'}
          </button>
          <button onClick={fetchData} style={{
            display:'flex', alignItems:'center', gap:'6px',
            padding:'8px 16px', borderRadius:'8px', border:'1px solid #e2e8f0',
            background:'#fff', color:'#475569', fontSize:'13px', fontWeight:'600', cursor:'pointer'
          }}>↻ Refresh</button>
        </div>
      </div>

      {/* ── KPI strip ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'16px', marginBottom:'28px' }}>
        {[
          {
            label: 'Active Projects', value: activeProjects.length,
            color: '#3b82f6', bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
            icon: '📁', click: () => navigate('/projects'),
          },
          {
            label: 'Completed Projects', value: completedProjects.length,
            color: '#16a34a', bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
            icon: '✅', click: () => navigate('/projects'),
          },
          {
            label: 'Overdue Projects', value: overdueProjects.length,
            color: overdueProjects.length > 0 ? '#dc2626' : '#16a34a',
            bg: overdueProjects.length > 0 ? 'linear-gradient(135deg,#fef2f2,#fee2e2)' : 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
            icon: overdueProjects.length > 0 ? '⚠️' : '✅',
            click: () => document.getElementById('overdue-projects-section')?.scrollIntoView({ behavior:'smooth' }),
          },
          {
            label: 'Overdue Tasks', value: overdueTasks.length,
            color: overdueTasks.length > 0 ? '#dc2626' : '#16a34a',
            bg: overdueTasks.length > 0 ? 'linear-gradient(135deg,#fef2f2,#fee2e2)' : 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
            icon: overdueTasks.length > 0 ? '🔴' : '✅',
            click: () => document.getElementById('overdue-tasks-section')?.scrollIntoView({ behavior:'smooth' }),
          },
          {
            label: 'Over-allocated', value: overallocated.length,
            color: overallocated.length > 0 ? '#d97706' : '#16a34a',
            bg: overallocated.length > 0 ? 'linear-gradient(135deg,#fffbeb,#fef3c7)' : 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
            icon: overallocated.length > 0 ? '⚡' : '✅',
            click: () => document.getElementById('overallocated-section')?.scrollIntoView({ behavior:'smooth' }),
          },
          {
            label: 'Total Tasks', value: totalTasks,
            color: '#8b5cf6', bg: 'linear-gradient(135deg,#f5f3ff,#ede9fe)',
            icon: '📋',
          },
          {
            label: 'Unplanned Tasks', value: unplannedData.total_unplanned_tasks,
            color: unplannedData.total_unplanned_tasks > 0 ? '#7c3aed' : '#16a34a',
            bg: unplannedData.total_unplanned_tasks > 0 ? 'linear-gradient(135deg,#f5f3ff,#ede9fe)' : 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
            icon: unplannedData.total_unplanned_tasks > 0 ? '🚨' : '✅',
            click: unplannedData.total_unplanned_tasks > 0 ? () => setShowUnplannedModal(true) : null,
          },
        ].map(card => (
          <div key={card.label}
            onClick={card.click}
            style={{
              background: card.bg, borderRadius:'16px', padding:'20px',
              cursor: card.click ? 'pointer' : 'default',
              border: `1px solid ${card.color}22`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={e => { if (card.click) { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.1)'; }}}
            onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.04)'; }}
          >
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontSize:'32px', fontWeight:'800', color:card.color, lineHeight:1 }}>
                  <AnimatedNumber value={card.value} />
                </div>
                <div style={{ fontSize:'12px', fontWeight:'600', color:'#64748b', marginTop:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  {card.label}
                </div>
              </div>
              <div style={{ fontSize:'28px', opacity:0.85 }}>{card.icon}</div>
            </div>
            {card.click && (
              <div style={{ fontSize:'11px', color:card.color, marginTop:'10px', fontWeight:'600' }}>
                {card.label.includes('Unplanned') ? 'Click to view ↑' : 'Click to view ↓'}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Two-column: RAG health + Completion ring ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'24px' }}>

        {/* RAG Project Health */}
        <div style={{ background:'#fff', borderRadius:'16px', padding:'24px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9' }}>
          <div style={{ fontWeight:'700', fontSize:'16px', color:'#0f172a', marginBottom:'20px', display:'flex', alignItems:'center', gap:'8px' }}>
            🚦 Project Health (RAG)
            <span style={{ fontSize:'11px', fontWeight:'600', color:'#94a3b8', background:'#f1f5f9', borderRadius:'20px', padding:'2px 8px' }}>
              Active projects only
            </span>
          </div>
          {projects.length === 0 ? (
            <div style={{ color:'#94a3b8', textAlign:'center', padding:'20px 0', fontSize:'14px' }}>No projects</div>
          ) : (
            <>
              {/* Stacked bar */}
              <div style={{ height:'24px', borderRadius:'12px', overflow:'hidden', display:'flex', marginBottom:'20px', background:'#f1f5f9' }}>
                {['Green','Amber','Red'].map(r => {
                  const pct = activeProjects.length > 0 ? (ragCounts[r] / activeProjects.length) * 100 : 0;
                  return pct > 0 ? (
                    <div key={r} title={`${ragCounts[r]} ${r}`}
                      style={{ width:`${pct}%`, background: RAG_CFG[r].color, transition:'width 0.6s ease' }} />
                  ) : null;
                })}
              </div>
              {/* Legend — clickable tiles */}
              <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
                {['Green','Amber','Red'].map(r => {
                  const isSelected = selectedRag === r;
                  const count = ragCounts[r];
                  return (
                    <div key={r}
                      onClick={() => count > 0 && toggleRag(r)}
                      style={{ display:'flex', alignItems:'center', gap:'8px', flex:1, minWidth:'100px',
                        background: RAG_CFG[r].bg, borderRadius:'12px', padding:'12px 16px',
                        cursor: count > 0 ? 'pointer' : 'default',
                        outline: isSelected ? `2px solid ${RAG_CFG[r].color}` : 'none',
                        outlineOffset: '2px',
                        transition: 'transform 0.15s, outline 0.15s',
                        transform: isSelected ? 'translateY(-2px)' : 'none',
                      }}
                      title={count > 0 ? `Click to see ${r} projects` : ''}
                    >
                      <span style={{ width:12, height:12, borderRadius:'50%', background:RAG_CFG[r].color, display:'inline-block', flexShrink:0 }} />
                      <div>
                        <div style={{ fontSize:'22px', fontWeight:'800', color:RAG_CFG[r].color }}>{count}</div>
                        <div style={{ fontSize:'11px', color:'#64748b', fontWeight:'600', textTransform:'uppercase' }}>
                          {r} {count > 0 && <span style={{ fontSize:'9px' }}>{isSelected ? '▲' : '▼'}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Project list — filtered by selected RAG tile */}
              {selectedRag && activeProjects.filter(p => (p.rag || 'Green') === selectedRag).length > 0 && (
                <div style={{ marginTop:'16px', display:'flex', flexDirection:'column', gap:'6px' }}>
                  <div style={{ fontSize:'11px', fontWeight:'700', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'4px' }}>
                    {selectedRag} projects ({activeProjects.filter(p => (p.rag || 'Green') === selectedRag).length})
                  </div>
                  {activeProjects.filter(p => (p.rag || 'Green') === selectedRag).map(p => (
                    <div key={p.project_id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                      padding:'8px 12px', borderRadius:'8px', background:'#f8fafc', border:`1px solid ${RAG_CFG[selectedRag].bg}`,
                      borderLeft: `3px solid ${RAG_CFG[selectedRag].color}` }}>
                      <Link to={`/projects/${p.project_id}`} style={{ fontWeight:'600', fontSize:'13px', color:'#1e293b', textDecoration:'none' }}>
                        {p.project_name}
                      </Link>
                      <RagDot rag={p.rag || 'Green'} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Completion ring + stats */}
        <div style={{ background:'#fff', borderRadius:'16px', padding:'24px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9' }}>
          <div style={{ fontWeight:'700', fontSize:'16px', color:'#0f172a', marginBottom:'20px' }}>
            📊 Overall Progress
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'28px' }}>
            {/* Donut */}
            <div style={{ position:'relative', flexShrink:0 }}>
              <MiniDonut pct={completionPct} size={100} stroke={10}
                color={completionPct >= 75 ? '#16a34a' : completionPct >= 50 ? '#d97706' : '#3b82f6'} />
              <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column',
                alignItems:'center', justifyContent:'center' }}>
                <div style={{ fontSize:'18px', fontWeight:'800', color:'#0f172a' }}>{completionPct}%</div>
                <div style={{ fontSize:'9px', color:'#94a3b8', fontWeight:'600', textTransform:'uppercase' }}>done</div>
              </div>
            </div>
            {/* Stats */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', flex:1 }}>
              {[
                { label:'Total Tasks', value:totalTasks, color:'#3b82f6', bg:'#eff6ff' },
                { label:'Completed', value:completedTasks, color:'#16a34a', bg:'#f0fdf4' },
                { label:'Overdue', value:overdueTasks.length, color:overdueTasks.length > 0 ? '#dc2626':'#16a34a', bg:overdueTasks.length > 0 ? '#fef2f2':'#f0fdf4' },
                { label:'Projects', value:projects.length, color:'#8b5cf6', bg:'#f5f3ff' },
              ].map(s => (
                <div key={s.label} style={{ background:s.bg, borderRadius:'10px', padding:'12px' }}>
                  <div style={{ fontSize:'22px', fontWeight:'800', color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:'11px', color:'#64748b', fontWeight:'600', marginTop:'2px', textTransform:'uppercase', letterSpacing:'0.3px' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── Overdue Projects ── */}
      {overdueProjects.length > 0 && (
        <div id="overdue-projects-section" style={{
          background:'#fff', borderRadius:'16px',
          boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
          border:'1px solid #fee2e2', borderLeft:'5px solid #dc2626',
          marginBottom:'20px', overflow:'hidden'
        }}>
          {/* Collapsed header — always visible */}
          <div onClick={() => toggleSection('overdueProjects')} style={{
            display:'flex', justifyContent:'space-between', alignItems:'center',
            padding:'16px 24px', cursor:'pointer',
            background: sections.overdueProjects ? '#fff' : '#fff5f5',
            transition:'background 0.15s',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <span style={{ fontSize:'18px' }}>⚠️</span>
              <span style={{ fontWeight:'700', fontSize:'15px', color:'#dc2626' }}>Overdue Projects</span>
              <span style={{ background:'#fef2f2', color:'#dc2626', borderRadius:'20px', padding:'2px 10px', fontSize:'13px', fontWeight:'700', border:'1px solid #fca5a5' }}>
                {overdueProjects.length}
              </span>
              {/* Severity pills summary */}
              {!sections.overdueProjects && (() => {
                const critical = overdueProjects.filter(p => daysAgo(p.planned_end_date) >= 30).length;
                const warning  = overdueProjects.filter(p => { const d = daysAgo(p.planned_end_date); return d >= 14 && d < 30; }).length;
                const mild     = overdueProjects.filter(p => daysAgo(p.planned_end_date) < 14).length;
                return (
                  <div style={{ display:'flex', gap:'6px', marginLeft:'4px' }}>
                    {critical > 0 && <span style={{ background:'#dc2626', color:'#fff', borderRadius:'20px', padding:'2px 8px', fontSize:'11px', fontWeight:'700' }}>{critical} critical</span>}
                    {warning  > 0 && <span style={{ background:'#ea580c', color:'#fff', borderRadius:'20px', padding:'2px 8px', fontSize:'11px', fontWeight:'700' }}>{warning} warning</span>}
                    {mild     > 0 && <span style={{ background:'#f59e0b', color:'#fff', borderRadius:'20px', padding:'2px 8px', fontSize:'11px', fontWeight:'700' }}>{mild} mild</span>}
                  </div>
                );
              })()}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <Link to="/projects" onClick={e => e.stopPropagation()} style={{ fontSize:'12px', fontWeight:'600', color:'#3b82f6', textDecoration:'none', padding:'5px 12px', border:'1px solid #bfdbfe', borderRadius:'6px', background:'#eff6ff' }}>
                View All →
              </Link>
              <span style={{ color:'#dc2626', fontSize:'13px', fontWeight:'700' }}>{sections.overdueProjects ? '▲' : '▼'}</span>
            </div>
          </div>
          {/* Expandable content */}
          {sections.overdueProjects && (
            <div style={{ padding:'0 24px 20px', display:'flex', flexDirection:'column', gap:'8px' }}>
              {overdueProjects.map(p => {
                const days = daysAgo(p.planned_end_date);
                const c = overdueColor(days);
                return (
                  <div key={p.project_id} style={{
                    display:'flex', alignItems:'center', gap:'14px',
                    padding:'12px 14px', borderRadius:'10px',
                    background:c.bg, border:`1px solid ${c.bar}33`
                  }}>
                    <div style={{ width:'3px', height:'36px', borderRadius:'2px', background:c.bar, flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <Link to={`/projects/${p.project_id}`} style={{ fontWeight:'700', fontSize:'13px', color:'#0f172a', textDecoration:'none' }}>
                        {p.project_name}
                      </Link>
                      <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>
                        {p.manager_name || 'Unassigned'} · Due {fmt(p.planned_end_date)}
                      </div>
                    </div>
                    <RagDot rag={p.rag || 'Red'} />
                    <div style={{ background:c.bar, color:'#fff', borderRadius:'20px', padding:'3px 12px', fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap', flexShrink:0 }}>
                      {days}d overdue
                    </div>
                    <Link to={`/projects/${p.project_id}`} style={{ padding:'5px 12px', borderRadius:'7px', background:'#0f172a', color:'#fff', fontSize:'11px', fontWeight:'600', textDecoration:'none', flexShrink:0 }}>View</Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Overdue Tasks ── */}
      {overdueTasks.length > 0 && (() => {
        // Group by project for manageable display
        const byProject = sortedOverdue.reduce((acc, t) => {
          const key = t.project_id;
          if (!acc[key]) acc[key] = { project_id: t.project_id, project_name: t.project_name, tasks: [] };
          acc[key].tasks.push(t);
          return acc;
        }, {});
        const groups = Object.values(byProject).sort((a, b) =>
          daysAgo(b.tasks[0].planned_end) - daysAgo(a.tasks[0].planned_end));
        return (
          <div id="overdue-tasks-section" style={{
            background:'#fff', borderRadius:'16px',
            boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
            border:'1px solid #fecaca', borderLeft:'5px solid #ef4444',
            marginBottom:'20px', overflow:'hidden'
          }}>
            {/* Section header */}
            <div onClick={() => toggleSection('overdueTasks')} style={{
              display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'16px 24px', cursor:'pointer',
              background: sections.overdueTasks ? '#fff' : '#fff5f5',
              transition:'background 0.15s',
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <span style={{ fontSize:'18px' }}>🔴</span>
                <span style={{ fontWeight:'700', fontSize:'15px', color:'#dc2626' }}>Overdue Tasks</span>
                <span style={{ background:'#fef2f2', color:'#dc2626', borderRadius:'20px', padding:'2px 10px', fontSize:'13px', fontWeight:'700', border:'1px solid #fca5a5' }}>
                  {overdueTasks.length}
                </span>
                {!sections.overdueTasks && (
                  <span style={{ fontSize:'12px', color:'#94a3b8', fontWeight:'500' }}>
                    across {groups.length} project{groups.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <span style={{ color:'#dc2626', fontSize:'13px', fontWeight:'700' }}>{sections.overdueTasks ? '▲' : '▼'}</span>
            </div>
            {/* Expanded: grouped by project */}
            {sections.overdueTasks && (
              <div style={{ padding:'0 24px 20px', display:'flex', flexDirection:'column', gap:'12px' }}>
                {groups.map(group => (
                  <div key={group.project_id} style={{ border:'1px solid #fecaca', borderRadius:'10px', overflow:'hidden' }}>
                    {/* Project group header */}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                      padding:'10px 14px', background:'#fef2f2', borderBottom:'1px solid #fecaca' }}>
                      <Link to={`/projects/${group.project_id}`} style={{ fontWeight:'700', fontSize:'13px', color:'#dc2626', textDecoration:'none' }}>
                        📁 {group.project_name}
                      </Link>
                      <span style={{ background:'#fef2f2', color:'#dc2626', border:'1px solid #fca5a5',
                        borderRadius:'20px', padding:'2px 10px', fontSize:'11px', fontWeight:'700' }}>
                        {group.tasks.length} overdue task{group.tasks.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {/* Tasks in this project */}
                    <div style={{ display:'flex', flexDirection:'column', gap:'0' }}>
                      {group.tasks.map((task, i) => {
                        const days = daysAgo(task.planned_end);
                        const c = overdueColor(days);
                        const priColor = task.priority === 'Critical' || task.priority === 'High' ? '#dc2626'
                          : task.priority === 'Medium' ? '#d97706' : '#64748b';
                        return (
                          <div key={task.task_id} style={{
                            display:'flex', alignItems:'center', gap:'12px', padding:'10px 14px',
                            borderBottom: i < group.tasks.length - 1 ? '1px solid #fef2f2' : 'none',
                            background:'#fff',
                          }}>
                            <div style={{ width:'3px', height:'32px', borderRadius:'2px', background:c.bar, flexShrink:0 }} />
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:'600', fontSize:'12px', color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                {task.task_name}
                              </div>
                              <div style={{ fontSize:'11px', color:'#64748b', marginTop:'1px' }}>
                                {task.assigned_to_name || 'Unassigned'} · Due {fmt(task.planned_end)}
                              </div>
                            </div>
                            <div style={{ width:'70px', flexShrink:0 }}>
                              <div style={{ height:'4px', background:'#e2e8f0', borderRadius:'99px', overflow:'hidden' }}>
                                <div style={{ height:'100%', width:`${task.progress}%`, background:'#3b82f6', borderRadius:'99px' }} />
                              </div>
                              <div style={{ fontSize:'10px', color:'#94a3b8', textAlign:'right', marginTop:'2px' }}>{task.progress}%</div>
                            </div>
                            <span style={{ fontSize:'10px', fontWeight:'700', color:priColor, background:`${priColor}15`, padding:'2px 8px', borderRadius:'20px', flexShrink:0 }}>
                              {task.priority}
                            </span>
                            <div style={{ background:c.bar, color:'#fff', borderRadius:'20px', padding:'2px 10px', fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap', flexShrink:0 }}>
                              {days}d
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Over-allocated Resources ── */}
      {overallocated.length > 0 && (
        <div id="overallocated-section" style={{
          background:'#fff', borderRadius:'16px',
          boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
          border:'1px solid #fde68a', borderLeft:'5px solid #f59e0b',
          marginBottom:'20px', overflow:'hidden'
        }}>
          {/* Section header */}
          <div onClick={() => toggleSection('overallocated')} style={{
            display:'flex', justifyContent:'space-between', alignItems:'center',
            padding:'16px 24px', cursor:'pointer',
            background: sections.overallocated ? '#fff' : '#fffbeb',
            transition:'background 0.15s',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <span style={{ fontSize:'18px' }}>⚡</span>
              <span style={{ fontWeight:'700', fontSize:'15px', color:'#92400e' }}>Over-allocated Resources</span>
              <span style={{ background:'#fef3c7', color:'#92400e', borderRadius:'20px', padding:'2px 10px', fontSize:'13px', fontWeight:'700', border:'1px solid #fde68a' }}>
                {overallocated.length}
              </span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <Link to="/allocation" onClick={e => e.stopPropagation()} style={{ fontSize:'12px', fontWeight:'600', color:'#3b82f6', textDecoration:'none', padding:'5px 12px', border:'1px solid #bfdbfe', borderRadius:'6px', background:'#eff6ff' }}>
                Open Allocation →
              </Link>
              <span style={{ color:'#92400e', fontSize:'13px', fontWeight:'700' }}>{sections.overallocated ? '▲' : '▼'}</span>
            </div>
          </div>

          {/* Expanded: one row per employee, projects as chips */}
          {sections.overallocated && (
            <div style={{ padding:'0 24px 20px', display:'flex', flexDirection:'column', gap:'8px' }}>
              {overallocated.map(emp => {
                const peak    = Math.max(...emp.tasks.map(t => t.total_allocation_in_period));
                const overPct = peak - 100;
                const isExpanded = expandedEmps[emp.employee_id];

                // Build per-project allocation map from tasks
                // A task is active today if ANY of these is true (mirrors Rules 3+4+6):
                //   1. planned_start <= today <= planned_end  (normal active task)
                //   2. planned_end < today AND no actual_end  (slipping — past due, still open)
                //   3. forecast_start <= today AND planned_start > today (forecast start earlier than planned)
                const todayMs = new Date(todayStr + 'T12:00:00').getTime();
                const isTaskActiveToday = (t) => {
                  const effectiveStart = t.forecast_start || t.planned_start;
                  const sMs = new Date((effectiveStart || todayStr) + 'T12:00:00').getTime();
                  const eMs = new Date((t.planned_end   || todayStr) + 'T12:00:00').getTime();
                  // isSlipping: planned_end in past, not yet completed (no actual_end)
                  const isSlipping = t.planned_end && t.planned_end < todayStr && !t.actual_end;
                  return isSlipping || (sMs <= todayMs && todayMs <= eMs);
                };
                const projectMap = emp.tasks.reduce((acc, t) => {
                  if (!acc[t.project_id]) acc[t.project_id] = {
                    project_id: t.project_id, project_name: t.project_name,
                    alloc: 0, activeToday: false
                  };
                  acc[t.project_id].alloc += t.allocation_percent;
                  if (isTaskActiveToday(t)) acc[t.project_id].activeToday = true;
                  return acc;
                }, {});
                const projects = Object.values(projectMap);

                // Active-today projects: combined alloc of active-today projects
                const activeTodayAlloc = projects
                  .filter(p => p.activeToday)
                  .reduce((s, p) => s + p.alloc, 0);
                // A project is "conflicting" if it is active today AND
                // the combined active-today allocation exceeds 100%
                const hasConflict = activeTodayAlloc > 100;

                // Sort: active-today first, then future/past
                const sortedProjects = [...projects].sort((a, b) => {
                  if (a.activeToday && !b.activeToday) return -1;
                  if (!a.activeToday && b.activeToday) return 1;
                  return 0;
                });

                return (
                  <div key={emp.employee_id} style={{ border:'1px solid #fde68a', borderRadius:'10px', overflow:'hidden' }}>
                    {/* Employee row */}
                    <div style={{ display:'flex', alignItems:'center', gap:'14px', padding:'12px 16px', background:'#fffbeb' }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', background:'#f59e0b',
                        color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:'13px', fontWeight:'700', flexShrink:0 }}>
                        {initials(emp.employee_name)}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:'700', fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>
                          {emp.employee_name}
                          {hasConflict && (
                            <span style={{ marginLeft:'8px', fontSize:'11px', fontWeight:'600',
                              color:'#dc2626', background:'#fef2f2', border:'1px solid #fca5a5',
                              borderRadius:'20px', padding:'1px 8px' }}>
                              ⚡ {activeTodayAlloc}% active today
                            </span>
                          )}
                        </div>
                        {/* Project chips — colour = whether active today + conflicting */}
                        <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                          {sortedProjects.map(p => {
                            const isConflicting = p.activeToday && hasConflict;
                            const isFuturePast  = !p.activeToday;
                            const bg     = isConflicting ? '#fef2f2' : isFuturePast ? '#f8fafc' : '#f0fdf4';
                            const color  = isConflicting ? '#dc2626' : isFuturePast ? '#94a3b8' : '#16a34a';
                            const border = isConflicting ? '#fca5a5' : isFuturePast ? '#e2e8f0' : '#86efac';
                            const icon   = isConflicting ? '⚡ ' : isFuturePast ? '' : '✓ ';
                            const title  = isConflicting
                              ? 'Active today — contributing to over-allocation'
                              : isFuturePast
                              ? 'Not active today (future or past dates)'
                              : 'Active today — within capacity';
                            return (
                              <Link key={p.project_id} to={`/projects/${p.project_id}`}
                                style={{ textDecoration:'none' }} title={title}>
                                <span style={{
                                  display:'inline-flex', alignItems:'center', gap:'3px',
                                  padding:'2px 9px', borderRadius:'20px', fontSize:'11px', fontWeight:'600',
                                  background: bg, color, border: `1px solid ${border}`,
                                }}>
                                  {icon}{p.project_name}
                                  <span style={{ opacity:0.65, fontSize:'10px' }}>{p.alloc}%</span>
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                        {/* Legend */}
                        <div style={{ display:'flex', gap:'12px', marginTop:'7px', flexWrap:'wrap' }}>
                          {[
                            { bg:'#fef2f2', color:'#dc2626', border:'#fca5a5', label:'⚡ Active today — over limit' },
                            { bg:'#f0fdf4', color:'#16a34a', border:'#86efac', label:'✓ Active today — ok' },
                            { bg:'#f8fafc', color:'#94a3b8', border:'#e2e8f0', label:'Not active today' },
                          ].map(l => (
                            <span key={l.label} style={{ display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'10px', color:'#94a3b8' }}>
                              <span style={{ display:'inline-block', width:10, height:10, borderRadius:'50%', background:l.bg, border:`1px solid ${l.border}` }} />
                              <span style={{ color:l.color, fontWeight:'600' }}>{l.label}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                      {/* Peak gauge */}
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
                        <div style={{ width:'100px' }}>
                          <div style={{ fontSize:'10px', color:'#92400e', fontWeight:'700', marginBottom:'3px', textAlign:'right' }}>
                            PEAK {peak}%
                          </div>
                          <div style={{ height:'6px', background:'#fde68a', borderRadius:'99px', overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${Math.min(peak / 2, 100)}%`,
                              background: peak > 150 ? '#dc2626' : peak > 120 ? '#ea580c' : '#f59e0b',
                              borderRadius:'99px' }} />
                          </div>
                        </div>
                        <span style={{ background:'#fef2f2', color:'#dc2626', border:'1px solid #fca5a5',
                          borderRadius:'20px', padding:'2px 8px', fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap' }}>
                          +{overPct}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Project health grid (all projects) ── */}
      <div style={{ background:'#fff', borderRadius:'16px',
        boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9', marginBottom:'20px', overflow:'hidden' }}>
        <div onClick={() => toggleSection('projects')} style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'16px 24px', cursor:'pointer',
          background: sections.projects ? '#fff' : '#f8fafc',
          transition:'background 0.15s',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <span style={{ fontSize:'18px' }}>📁</span>
            <span style={{ fontWeight:'700', fontSize:'15px', color:'#0f172a' }}>
              {showCompleted ? 'All Projects' : 'Active Projects'}
            </span>
            <span style={{ fontSize:'12px', fontWeight:'600', color:'#94a3b8', background:'#f1f5f9', borderRadius:'20px', padding:'2px 8px', border:'1px solid #e2e8f0' }}>
              {dashProjects.length}
            </span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <Link to="/projects" onClick={e => e.stopPropagation()} style={{ fontSize:'12px', fontWeight:'600', color:'#3b82f6', textDecoration:'none', padding:'5px 12px', border:'1px solid #bfdbfe', borderRadius:'6px', background:'#eff6ff' }}>
              Manage Projects →
            </Link>
            <span style={{ color:'#64748b', fontSize:'13px', fontWeight:'700' }}>{sections.projects ? '▲' : '▼'}</span>
          </div>
        </div>
        {sections.projects && (dashProjects.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px', color:'#94a3b8', fontSize:'14px' }}>No projects yet</div>
        ) : (
          <div style={{ padding:'0 24px 24px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:'12px' }}>
            {dashProjects.map(p => {
              const pct = p.total_tasks > 0 ? Math.round((p.completed_tasks / p.total_tasks) * 100) : 0;
              const barColor = pct >= 75 ? '#16a34a' : pct >= 50 ? '#d97706' : '#3b82f6';
              const isOverdue = p.planned_end_date && p.status !== 'Completed' && new Date(p.planned_end_date) < today;
              return (
                <Link key={p.project_id} to={`/projects/${p.project_id}`} style={{ textDecoration:'none' }}>
                  <div style={{
                    padding:'16px', borderRadius:'12px', border:`1px solid ${isOverdue ? '#fecaca' : '#f1f5f9'}`,
                    background: isOverdue ? '#fff5f5' : '#fafbfc',
                    transition:'box-shadow 0.15s, transform 0.15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow=''; e.currentTarget.style.transform=''; }}
                  >
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
                      <div style={{ fontWeight:'700', fontSize:'13px', color:'#0f172a',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, marginRight:'8px' }}>
                        {p.project_name}
                      </div>
                      {COMPLETED_STATUSES.includes(p.status)
                        ? <span style={{ fontSize:'11px', fontWeight:'700', color:'#16a34a', background:'#f0fdf4', border:'1px solid #86efac', borderRadius:'20px', padding:'2px 8px', flexShrink:0 }}>{p.status}</span>
                        : <RagDot rag={p.rag || 'Green'} />
                      }
                    </div>
                    <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'10px' }}>
                      {p.manager_name || 'Unassigned'} · {p.total_tasks || 0} tasks
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px' }}>
                      <span style={{ fontSize:'11px', color:'#94a3b8', fontWeight:'600' }}>PROGRESS</span>
                      <span style={{ fontSize:'11px', fontWeight:'700', color:barColor }}>{pct}%</span>
                    </div>
                    <div style={{ height:'5px', background:'#e2e8f0', borderRadius:'99px', overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${pct}%`, background:barColor, borderRadius:'99px' }} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      {/* ── Unplanned Tasks Modal (unchanged logic, restyled) ── */}
      {showUnplannedModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.6)', zIndex:1000,
          display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
          onClick={() => setShowUnplannedModal(false)}>
          <div style={{ background:'#fff', borderRadius:'20px', width:'100%', maxWidth:'900px',
            maxHeight:'88vh', display:'flex', flexDirection:'column',
            boxShadow:'0 24px 64px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:'24px 28px 16px', borderBottom:'1px solid #f1f5f9',
              display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <h3 style={{ margin:0, fontSize:'20px', fontWeight:'800', color:'#0f172a' }}>🚨 Unplanned Tasks</h3>
                <p style={{ margin:'4px 0 0', fontSize:'13px', color:'#64748b' }}>
                  {unplannedData.total_unplanned_tasks} tasks · {unplannedData.total_planned_hours}h planned · {unplannedData.total_actual_hours}h actual
                </p>
              </div>
              <button onClick={() => setShowUnplannedModal(false)} style={{ background:'none', border:'none',
                fontSize:'22px', cursor:'pointer', color:'#94a3b8' }}>✕</button>
            </div>
            <div style={{ display:'flex', gap:'0', background:'#f8f5ff', borderBottom:'1px solid #ede9fe' }}>
              {[
                { label:'Planned Hours', value:`${unplannedData.total_planned_hours}h`, color:'#7c3aed', bg:'#f5f3ff' },
                { label:'Actual Hours', value:`${unplannedData.total_actual_hours}h`, color:'#0369a1', bg:'#eff6ff' },
              ].map(s => (
                <div key={s.label} style={{ flex:1, padding:'16px 24px', background:s.bg }}>
                  <div style={{ fontSize:'28px', fontWeight:'800', color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:'12px', color:'#64748b', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.5px' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ overflowY:'auto', flex:1, padding:'16px 0' }}>
              {unplannedData.tasks.map((task, i) => (
                <div key={task.task_id} style={{ padding:'12px 28px', borderBottom:'1px solid #f8fafc',
                  background: i % 2 === 0 ? '#fff' : '#fafbfc', display:'flex', alignItems:'center', gap:'16px' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:'600', fontSize:'13px', color:'#0f172a' }}>{task.task_name}</div>
                    <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>
                      <Link to={`/projects/${task.project_id}`} onClick={() => setShowUnplannedModal(false)}
                        style={{ color:'#3b82f6', textDecoration:'none', fontWeight:'600' }}>{task.project_name}</Link>
                      {task.assigned_to_name && <> · {task.assigned_to_name}</>}
                    </div>
                    {task.unplanned_reason && (
                      <div style={{ fontSize:'11px', color:'#7c3aed', marginTop:'3px', fontStyle:'italic' }}>
                        "{task.unplanned_reason}"
                      </div>
                    )}
                  </div>
                  <span style={{ background:'#fef3c7', color:'#92400e', borderRadius:'20px',
                    padding:'3px 12px', fontSize:'12px', fontWeight:'700', flexShrink:0 }}>
                    {task.allocation_percent}%
                  </span>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:'16px', fontWeight:'800', color:'#7c3aed' }}>{task.planned_hours}h</div>
                    <div style={{ fontSize:'10px', color:'#94a3b8' }}>planned</div>
                  </div>
                  {task.actual_start && (
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:'16px', fontWeight:'800', color:'#0369a1' }}>{task.actual_hours}h</div>
                      <div style={{ fontSize:'10px', color:'#94a3b8' }}>actual</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ padding:'14px 28px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'flex-end' }}>
              <button onClick={() => setShowUnplannedModal(false)} style={{
                padding:'8px 20px', borderRadius:'8px', border:'1px solid #e2e8f0',
                background:'#f8fafc', color:'#475569', fontSize:'13px', fontWeight:'600', cursor:'pointer'
              }}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
